import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type DocumentNode,
  type ErrorLike,
  NetworkStatus,
  type OperationVariables,
} from '@apollo/client';
import { useQuery } from '@apollo/client/react';

type UseDashboardListOptions<TData, TVariables> = {
  query: DocumentNode;
  variables: Omit<TVariables, 'limit' | 'offset'>;
  dataKey: keyof TData;
  countKey: keyof TData;
  skip?: boolean;
  limit?: number;
  fetchPolicy?: 'cache-first' | 'network-only';
};

export const useDashboardList = <
  TData extends Record<string, any>,
  TVariables extends OperationVariables,
  TItem,
>({
  query,
  variables,
  dataKey,
  countKey,
  skip = false,
  limit = 4,
  fetchPolicy,
}: UseDashboardListOptions<TData, TVariables>) => {
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const loadMoreButtonRef = useRef<HTMLButtonElement>(null);
  const [visibleLimit, setVisibleLimit] = useState(limit);
  // Set once the server returns an empty page. The `*_aggregate` count can
  // exceed the rows row-level permissions actually return for this user, so
  // `cachedItems.length < totalCount` can stay true forever. Without this the
  // auto-fetch effect re-fires `fetchMore` on every networkStatus settle
  // (flickering the card title) AND `hasMore` stays true (a dead "Load more"
  // button + an inflated count badge). State-backed so flipping it re-renders;
  // reset whenever the query variables change (new filter / user).
  const [reachedServerEnd, setReachedServerEnd] = useState(false);

  const queryVariables = useMemo(
    () =>
      ({
        ...variables,
        limit,
        offset: 0,
      } as unknown as TVariables),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(variables), limit],
  );

  // Mirror of the current query variables, read inside async `fetchMore`
  // callbacks to ignore results from a request whose variables have since
  // changed (filter swap / user change mid-flight).
  const queryVariablesRef = useRef(queryVariables);
  queryVariablesRef.current = queryVariables;

  useEffect(() => {
    setReachedServerEnd(false);
    setVisibleLimit(limit);
  }, [queryVariables, limit]);

  const { data, fetchMore, networkStatus, refetch } = useQuery<
    TData,
    TVariables
  >(query, {
    variables: queryVariables,
    skip,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: fetchPolicy ?? 'cache-first',
  });

  const cachedItems: TItem[] = useMemo(
    () => (data?.[dataKey] as TItem[] | undefined) ?? [],
    [data, dataKey],
  );
  const items = useMemo(
    () => cachedItems.slice(0, visibleLimit),
    [cachedItems, visibleLimit],
  );
  const rawTotalCount: number =
    (data?.[countKey] as { aggregate: { count: number } })?.aggregate?.count ??
    0;
  // Once the server is exhausted, trust the rows we actually got over the
  // aggregate (which can over-count rows hidden by row-level permissions),
  // so the count badge and `hasMore` reflect what the user can really see.
  const totalCount = reachedServerEnd
    ? Math.min(rawTotalCount, cachedItems.length)
    : rawTotalCount;
  const hasMore = items.length < totalCount;

  useEffect(() => {
    if (
      skip ||
      reachedServerEnd ||
      networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore
    ) {
      return;
    }

    const targetCount = visibleLimit + limit;

    if (
      cachedItems.length < rawTotalCount &&
      cachedItems.length < targetCount
    ) {
      // Snapshot the variables this fetch was issued for, so async callbacks
      // can ignore results that arrive after the variables changed.
      const requestedFor = queryVariables;
      fetchMore({
        variables: {
          offset: cachedItems.length,
          includeCount: false,
        } as unknown as Partial<TVariables>,
        // Keep the cache updater pure — just merge the new page.
        updateQuery: (prev, { fetchMoreResult }) => {
          const nextRows = fetchMoreResult
            ? ((fetchMoreResult as TData)[dataKey] as TItem[])
            : [];
          if (nextRows.length === 0) {
            return prev;
          }
          const previousData = prev as TData;
          return {
            ...previousData,
            [dataKey]: [...(previousData[dataKey] as TItem[]), ...nextRows],
          } as typeof prev;
        },
      })
        .then(result => {
          // Ignore a result whose variables have since changed (filter swap
          // mid-flight) — otherwise we'd wedge auto-fetch off for the new query.
          if (queryVariablesRef.current !== requestedFor) {
            return;
          }
          // Empty page → the server's count exceeds what permissions let
          // this user read. Flip the flag here (not in updateQuery) so the
          // cache updater stays pure and the transition fires exactly once.
          const nextRows = (result.data as TData | undefined)?.[dataKey] as
            | TItem[]
            | undefined;
          if (!nextRows?.length) {
            setReachedServerEnd(true);
          }
        })
        .catch((err: unknown) => {
          if (queryVariablesRef.current !== requestedFor) {
            return;
          }
          // `fetchMore` rejections do NOT reach `useQuery`'s `onError`, so
          // surface the error here and stop auto-fetching to avoid a
          // retry-loop on a persistent failure.
          setErrorLike(err instanceof Error ? err : new Error(String(err)));
          setReachedServerEnd(true);
        });
    }
  }, [
    cachedItems.length,
    dataKey,
    fetchMore,
    limit,
    networkStatus,
    queryVariables,
    rawTotalCount,
    reachedServerEnd,
    skip,
    visibleLimit,
  ]);

  const loadMore = useCallback(() => {
    setVisibleLimit(prev => prev + limit);
  }, [limit]);

  // Cards refetch after a row action (claim/pass/accept/reject). `refetch`
  // re-issues the base query at offset 0, discarding the fetchMore-merged
  // pages — so also reset the pagination latch + visible window, otherwise
  // `reachedServerEnd` stays true and the list wedges at `limit` rows with a
  // dead "Load more".
  const refetchAndReset = useCallback(
    (...args: Parameters<typeof refetch>) => {
      setReachedServerEnd(false);
      setVisibleLimit(limit);
      return refetch(...args);
    },
    [refetch, limit],
  );

  return useMemo(
    () => ({
      items,
      totalCount,
      hasMore,
      loadMore,
      loadMoreButtonRef,
      loading: networkStatus === NetworkStatus.loading,
      isLoadingMore:
        networkStatus === NetworkStatus.fetchMore &&
        items.length < visibleLimit,
      error: apolloError,
      setError: setErrorLike,
      networkStatus,
      refetch: refetchAndReset,
    }),
    [
      items,
      totalCount,
      hasMore,
      loadMore,
      networkStatus,
      visibleLimit,
      apolloError,
      refetchAndReset,
    ],
  );
};
